import { v4 as uuidv4 } from 'uuid'
import { format as formatDate } from 'date-fns'
import moment from 'moment-timezone'
import { checkE164 } from '../../../../utilities'

const findNameOrDefault = (list, key, defaultName) => {
  return list.find(li => li.id === key)?.name ?? defaultName
}

export const getCategory = (categories, key) => {
  return findNameOrDefault(categories, key, 'No Category')
}

export const getType = (types, key) => {
  return findNameOrDefault(types, key, 'No Type')
}

export const getInbox = (inboxes, key) => {
  return findNameOrDefault(inboxes, key, 'Select an inbox')
}

const userTz = moment.tz.guess()
// Default values that show for new integrations
export const INITIAL_VALUES = {
  name: '',
  account_id: '',
  api_key: '',
  permissions: [],
  widget_config: {
    inbox_id: null,
    timeout: '300' /* system default is 5 mins */,
    task_config: {
      category_id: null,
      type_id: null,
      assignee_id: null
    },
    callback_task_config: null,
    timezone: userTz ?? null,
    hours_of_operation: [],
    messages: {
      kb_title: '',
      kb_intro: '',
      kb_send_message: '',
      kb_article_name: '',
      kb_article_name_plural: '',
      online_intro: '',
      offline_intro: '',
      offline_intro_end: '',
      m_offline_intro: '',
      m_offline_intro_end: '',
      offline_submit: ''
    },
    outside_hours_behaviour: 'create-task',
    topics: [],
    questions: [],
    sms_number: '',
    voice_number: '',
    security: 'normal',
    post_survey_id: null,
    theme: {
      start_color: '#f1f1f1',
      end_color: '#f1f1f1',
      location: 'bottom-right'
    }
  }
}

// Adds ids to topics to enable editing
const topicsToArray = topics => {
  const withIDs = (topics ?? []).map(topic => [uuidv4(), topic])
  return withIDs
}

// Converts time to date object
const asDate = time => {
  const [hours, minutes] = time.split(':')
  return new Date(0, 0, 0, +hours, +minutes)
}

// Adds ids to outside hours
export const hoursToObject = hours => {
  const withIDs = (hours ?? []).map(hour => ({ id: uuidv4(), ...hour }))

  return withIDs.reduce((prev, curr) => {
    prev[curr.id] = {
      start: asDate(curr.start),
      end: asDate(curr.end),
      days: curr.days ?? DAYS
    }
    return prev
  }, {})
}

// Tries to format the integration from the api into a usable format
export const formatDataFromAPI = data => {
  return {
    ...INITIAL_VALUES,
    ...data,
    widget_config: {
      ...INITIAL_VALUES.widget_config,
      ...(data.widget_config ?? {}),
      messages: data.widget_config?.messages
        ? {
            ...INITIAL_VALUES.widget_config.messages,
            ...data.widget_config.messages
          }
        : { ...INITIAL_VALUES.widget_config.messages },
      timeout:
        data.widget_config?.timeout?.toString() ??
        INITIAL_VALUES.widget_config.timeout,
      task_config:
        data.widget_config?.task_config ??
        INITIAL_VALUES.widget_config.task_config,
      callback_task_config:
        data.widget_config?.callback_task_config ??
        INITIAL_VALUES.widget_config.callback_task_config,
      sms_number:
        data.widget_config?.sms_number ??
        INITIAL_VALUES.widget_config.sms_number,
      voice_number:
        data.widget_config?.voice_number ??
        INITIAL_VALUES.widget_config.voice_number,
      hours_of_operation: hoursToObject(data.widget_config?.hours_of_operation),
      outside_hours_behaviour:
        data.widget_config?.outside_hours_behaviour ??
        INITIAL_VALUES.widget_config.outside_hours_behaviour,
      topics: topicsToArray(data.widget_config?.topics),
      questions: data.widget_config?.questions
        ? [...data.widget_config?.questions]
        : INITIAL_VALUES.widget_config.questions,
      security:
        data.widget_config?.security ?? INITIAL_VALUES.widget_config.security,
      theme: {
        ...(data.widget_config?.theme ?? INITIAL_VALUES.widget_config.theme),
        location:
          data.widget_config?.theme.location ??
          INITIAL_VALUES.widget_config.theme.location
      }
    }
  }
}

export const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
export const MIN_TIME = new Date(0, 0, 0, 0, 0)
export const MAX_TIME = new Date(0, 0, 0, 23, 59)

// Converts hours object to array for api
export const formatHoursForAPI = hours => {
  const arr = Object.values(hours)

  return arr.map(({ start, end, days }) => {
    const formatting = 'HH:mm'
    const startTime = formatDate(start, formatting)
    const endTime = formatDate(end, formatting)
    const fDays = days.length === DAYS.length ? null : days

    return {
      start: startTime,
      end: endTime,
      days: fDays
    }
  })
}

// Formatting theme colors to work with api
const formatThemeForAPI = theme => {
  const { start_color, end_color, location } = theme
  const start = start_color.replace('#', '')
  const end = end_color.replace('#', '')

  return {
    start_color: start,
    end_color: end,
    location
  }
}

// Removes empty strings
const formatMessagesForAPI = messages => {
  Object.keys(messages).forEach(key => {
    if (!messages[key]) {
      delete messages[key]
    }
  })

  return messages
}

// Re-formats data for upload
export const formatPayload = (
  values,
  messagesValues,
  enabled,
  withKB,
  disableLiveChat
) => {
  if (!values.name.length) {
    return { error: 'Please enter a name' }
  }

  const data = {
    name: values.name,
    permissions: values.permissions,
    widget_config: null
  }

  if (!enabled) {
    return { data }
  } else if (!values.widget_config.inbox_id) {
    return { error: 'Please select a default inbox' }
  }

  data.widget_config = {
    messages: formatMessagesForAPI(messagesValues),
    inbox_id: values.widget_config.inbox_id,
    timeout: +values.widget_config.timeout || 300,
    with_kb: withKB,
    disable_live_chat: disableLiveChat,
    task_config: {
      ...values.widget_config.task_config
    },
    callback_task_config: null,
    timezone: values.widget_config.timezone,
    sms_number: values.widget_config.sms_number
      ? checkE164(values.widget_config.sms_number)[1]
      : null,
    voice_number: values.widget_config.voice_number
      ? checkE164(values.widget_config.voice_number)[1]
      : null,
    hours_of_operation: formatHoursForAPI(
      values.widget_config.hours_of_operation
    ),
    outside_hours_behaviour: values.widget_config.outside_hours_behaviour,
    topics: null,
    questions: [...values.widget_config.questions],
    security: values.widget_config.security,
    post_survey_id: values.widget_config.post_survey_id,
    theme: formatThemeForAPI(values.widget_config.theme)
  }

  return { data }
}

const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60 * SECONDS_PER_MINUTE
export const TIMEOUT_OPTIONS = {
  [`${5 * SECONDS_PER_MINUTE}`]: '5 minutes (not recommended)',
  [`${15 * SECONDS_PER_MINUTE}`]: '15 minutes',
  [`${30 * SECONDS_PER_MINUTE}`]: '30 minutes (recommended)',
  [`${MINUTES_PER_HOUR}`]: '1 hour',
  [`${2 * MINUTES_PER_HOUR}`]: '2 hours (not recommended)'
}
export const OUTSIDE_HOURS_BEHAVIOUR = {
  'leave-message': 'Leave message',
  'create-task': 'Create task'
}
export const SECURITY_OPTIONS = {
  highest: 'Highest',
  normal: 'Normal (default)',
  relaxed: 'Relaxed'
}
export const LOCATION_OPTIONS = {
  'bottom-right': 'Bottom Right',
  'bottom-left': 'Bottom Left',
  'top-left': 'Top Left',
  'top-right': 'Top Right'
}
